import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { NgDialogModule, TypedFormControl } from '@principle-theorem/ng-shared';
import {
  type IAcceptedImportProposal,
  ImportExistingContent,
  ImportProposalMatching,
  WebsiteSource,
  WebsiteUrlRejectionReason,
} from '@principle-theorem/principle-core';
import {
  ImportFromWebsiteStatus,
  type IAIReceptionistKnowledgeBase,
  type IImportProposal,
  ImportFailureReason,
  type IPractice,
  type IWebsiteSnapshot,
  type IWebsiteSource,
} from '@principle-theorem/principle-core/interfaces';
import { toMoment, toTimestamp, type WithRef } from '@principle-theorem/shared';
import { debounceTime } from 'rxjs/operators';
import {
  AiReceptionistAiCallableService,
  AiReceptionistCallableError,
  AiReceptionistCallableErrorCode,
} from '../../services/ai-receptionist-ai-callable.service';
import { AiReceptionistWebsiteSnapshotService } from '../../services/ai-receptionist-website-snapshot.service';
import { ImportReview, type IImportReviewEntry } from './import-review';

export interface IAiReceptionistImportFromWebsiteDialogData {
  practice: WithRef<IPractice>;
  knowledgeBase: IAIReceptionistKnowledgeBase;
}

/**
 * What the practice accepted from the review. The dialog never persists it;
 * the editor store applies it to its unsaved Knowledge Base.
 */
export interface IAiReceptionistImportFromWebsiteDialogResult {
  proposal: IImportProposal;
  accepted: IAcceptedImportProposal;
}

export enum AiReceptionistImportDialogPhase {
  Idle = 'idle',
  Running = 'running',
  Proposal = 'proposal',
  Empty = 'empty',
  Error = 'error',
}

const emptyReasonCopy: Record<ImportFailureReason, string> = {
  [ImportFailureReason.FetchFailed]: `We couldn't read that website. Check the address, or try again later.`,
  [ImportFailureReason.ExtractionFailed]: `We read the website but couldn't turn it into suggestions. Please try again.`,
  [ImportFailureReason.NoUsableContent]: `We couldn't find any usable English content on that website.`,
};
const urlRejectionCopy: Record<WebsiteUrlRejectionReason, string> = {
  [WebsiteUrlRejectionReason.Invalid]:
    'Enter a valid website address, such as www.example.com.',
  [WebsiteUrlRejectionReason.UnsupportedHost]:
    'Enter a public website address. IP addresses and local hosts are not supported.',
  [WebsiteUrlRejectionReason.TooLong]:
    'That address is too long to be a website address.',
};
const freshReadRunningCopy =
  'Reading your website… This usually takes under a minute, but can take a few minutes for larger sites.';
const lastReadRunningCopy =
  'Preparing suggestions from your last website read…';
const emptyUrlCopy = 'Enter your practice website address.';
const unavailableCopy = `Website import isn't available for this practice.`;
const unknownErrorCopy = 'Something went wrong. Please try again.';
// How long typing pauses before the stored read of the new address is looked up
const snapshotLookupDebounceMs = 400;

/**
 * Runs a Website Import and lets the practice review the Import Proposal
 * field by field. It only ever returns what was accepted: nothing is saved
 * here, Provenance is shown and then forgotten, and the editor's existing
 * Save is the one path to Firestore and the AI Receptionist.
 */
@Component({
  selector: 'pr-ai-receptionist-import-from-website-dialog',
  imports: [
    NgDialogModule,
    MatCheckboxModule,
    MatProgressBarModule,
    MatRadioModule,
  ],
  templateUrl: './ai-receptionist-import-from-website-dialog.component.html',
  styleUrls: ['./ai-receptionist-import-from-website-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiReceptionistImportFromWebsiteDialogComponent {
  private readonly _dialogRef = inject(
    MatDialogRef<
      AiReceptionistImportFromWebsiteDialogComponent,
      IAiReceptionistImportFromWebsiteDialogResult | undefined
    >
  );
  private readonly _callable = inject(AiReceptionistAiCallableService);
  private readonly _snapshotService = inject(
    AiReceptionistWebsiteSnapshotService
  );
  private readonly _data =
    inject<IAiReceptionistImportFromWebsiteDialogData>(MAT_DIALOG_DATA);
  private readonly _websiteUrl = signal(this._data.practice.websiteUrl ?? '');
  private readonly _urlTouched = signal(false);
  private readonly _snapshot = signal<WithRef<IWebsiteSnapshot> | undefined>(
    undefined
  );
  private readonly _proposal = signal<IImportProposal | undefined>(undefined);
  private readonly _reviewEntries = signal<IImportReviewEntry[]>([]);
  private readonly _selectedIds = signal<Set<string>>(new Set());
  private readonly _emptyReason = signal<ImportFailureReason | undefined>(
    undefined
  );
  private readonly _error = signal<AiReceptionistCallableError | undefined>(
    undefined
  );
  private readonly _reusingLastRead = signal(false);
  /** The address the last snapshot lookup was for, and that lookup. */
  private _snapshotLookup: { address: string; done: Promise<void> } = {
    address: '',
    done: Promise.resolve(),
  };

  readonly phases = AiReceptionistImportDialogPhase;
  readonly phase = signal(AiReceptionistImportDialogPhase.Idle);
  readonly urlControl = new TypedFormControl<string>(this._websiteUrl());
  readonly useSnapshot = signal(true);

  readonly normalisation = computed(() =>
    WebsiteSource.normaliseUrl(this._websiteUrl())
  );

  readonly urlError = computed(() => {
    if (this._websiteUrl().trim() === '') {
      return emptyUrlCopy;
    }
    const normalisation = this.normalisation();
    return normalisation.ok
      ? undefined
      : urlRejectionCopy[normalisation.reason];
  });

  readonly showUrlError = computed(
    () => this._urlTouched() && this.urlError() !== undefined
  );

  readonly showPracticeSettingsHint = computed(() => {
    const normalisation = this.normalisation();
    if (!normalisation.ok) {
      return false;
    }
    const practiceUrl = WebsiteSource.normaliseUrl(
      this._data.practice.websiteUrl ?? ''
    );
    return !practiceUrl.ok || practiceUrl.url !== normalisation.url;
  });

  readonly snapshotUsable = computed(() => {
    const normalisation = this.normalisation();
    const snapshot = this._snapshot();
    if (!normalisation.ok || !snapshot) {
      return false;
    }
    try {
      return WebsiteSource.isSnapshotUsable(
        snapshot,
        normalisation.source,
        toTimestamp()
      );
    } catch {
      // A malformed stored document is not offered for reuse
      return false;
    }
  });

  readonly snapshotReadAgo = computed(() => {
    const snapshot = this._snapshot();
    if (!snapshot || !this.snapshotUsable()) {
      return '';
    }
    return toMoment(snapshot.fetchedAt).fromNow();
  });

  readonly readingLastWebsiteRead = computed(
    () => this.snapshotUsable() && this.useSnapshot()
  );

  readonly canImport = computed(
    () =>
      this.normalisation().ok &&
      this.phase() === AiReceptionistImportDialogPhase.Idle
  );

  readonly runningMessage = computed(() =>
    this._reusingLastRead() ? lastReadRunningCopy : freshReadRunningCopy
  );

  readonly proposalHeading = computed(() => {
    const proposal = this._proposal();
    return proposal ? ImportReview.heading(proposal) : '';
  });

  readonly reviewGroups = computed(() =>
    ImportReview.groupEntries(this._reviewEntries(), this._selectedIds())
  );

  readonly selectedCount = computed(() => this._selectedIds().size);

  readonly emptyMessage = computed(() => {
    const reason = this._emptyReason();
    return reason ? emptyReasonCopy[reason] : undefined;
  });

  readonly errorMessage = computed(() => {
    const error = this._error();
    return error ? this._errorCopy(error) : undefined;
  });

  readonly canRetry = computed(() => {
    const code = this._error()?.code;
    return (
      code !== AiReceptionistCallableErrorCode.PermissionDenied &&
      code !== AiReceptionistCallableErrorCode.FailedPrecondition
    );
  });

  constructor() {
    this.urlControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((address) => this._websiteUrl.set(address));
    this.urlControl.valueChanges
      .pipe(debounceTime(snapshotLookupDebounceMs), takeUntilDestroyed())
      .subscribe(() => void this._snapshotLookupForAddress());
    void this._lookupSnapshot();
  }

  urlBlurred(): void {
    this._urlTouched.set(true);
    void this._snapshotLookupForAddress();
  }

  async runImport(): Promise<void> {
    const normalisation = this.normalisation();
    if (!normalisation.ok || !this.canImport()) {
      return;
    }
    this._dialogRef.disableClose = true;
    this.phase.set(AiReceptionistImportDialogPhase.Running);
    // The address may have changed since its stored read was looked up
    await this._snapshotLookupForAddress();
    const useSnapshot = this.readingLastWebsiteRead();
    this._reusingLastRead.set(useSnapshot);
    try {
      const response = await this._callable.importFromWebsite({
        practiceRef: this._data.practice.ref,
        websiteUrl: normalisation.url,
        useSnapshot,
        existingContent: ImportExistingContent.existingContentOf(
          this._data.knowledgeBase
        ),
      });
      if (response.status === ImportFromWebsiteStatus.Empty) {
        this._emptyReason.set(response.reason);
        this.phase.set(AiReceptionistImportDialogPhase.Empty);
        return;
      }
      this._setProposal(response.proposal);
      this.phase.set(AiReceptionistImportDialogPhase.Proposal);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Website import failed', error);
      this._error.set(
        error instanceof AiReceptionistCallableError
          ? error
          : AiReceptionistCallableError.from(error)
      );
      this.phase.set(AiReceptionistImportDialogPhase.Error);
    } finally {
      this._dialogRef.disableClose = false;
    }
  }

  tryAgain(): void {
    this._emptyReason.set(undefined);
    this._error.set(undefined);
    this.phase.set(AiReceptionistImportDialogPhase.Idle);
    // A fresh read has just stored a snapshot, so offer it this time
    void this._lookupSnapshot();
  }

  toggleItem(id: string, checked: boolean): void {
    const selected = new Set(this._selectedIds());
    if (checked) {
      selected.add(id);
    } else {
      selected.delete(id);
    }
    this._selectedIds.set(selected);
  }

  applySelected(): void {
    const proposal = this._proposal();
    if (!proposal) {
      return;
    }
    const accepted = ImportReview.toAccepted(
      this._reviewEntries(),
      this._selectedIds()
    );
    this._dialogRef.close({ proposal, accepted });
  }

  /**
   * Looks up the stored read of the address now in the field unless a lookup
   * for it already ran or is running, and waits for that lookup.
   */
  private _snapshotLookupForAddress(): Promise<void> {
    return this._snapshotLookup.address === this._websiteUrl()
      ? this._snapshotLookup.done
      : this._lookupSnapshot();
  }

  private _lookupSnapshot(): Promise<void> {
    this._snapshotLookup = {
      address: this._websiteUrl(),
      done: this._readSnapshotForAddress(),
    };
    return this._snapshotLookup.done;
  }

  private async _readSnapshotForAddress(): Promise<void> {
    const address = this._websiteUrl();
    const normalisation = this.normalisation();
    if (!normalisation.ok) {
      this._snapshot.set(undefined);
      return;
    }
    const snapshot = await this._readSnapshot(normalisation.source);
    if (this._websiteUrl() !== address) {
      // The address changed while this lookup was in flight
      return;
    }
    this._snapshot.set(snapshot);
    this.useSnapshot.set(true);
  }

  /**
   * A failed pre-read must never block the import; the import reads the
   * website fresh instead.
   */
  private async _readSnapshot(
    source: IWebsiteSource
  ): Promise<WithRef<IWebsiteSnapshot> | undefined> {
    try {
      return await this._snapshotService.getLatestSnapshot(
        this._data.practice,
        source
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to read the stored website snapshot', error);
      return;
    }
  }

  /**
   * The proposal is linked to the Knowledge Base being edited first, so an
   * item asking an existing question or titled like an existing section is
   * reviewed, and applied, as an update to it rather than as a second copy.
   */
  private _setProposal(extracted: IImportProposal): void {
    const proposal = ImportProposalMatching.linkMatchingExisting(
      extracted,
      this._data.knowledgeBase
    );
    const entries = ImportReview.buildEntries(
      proposal,
      this._data.knowledgeBase
    );
    this._proposal.set(proposal);
    this._reviewEntries.set(entries);
    this._selectedIds.set(ImportReview.initialSelection(entries));
  }

  private _errorCopy(error: AiReceptionistCallableError): string {
    switch (error.code) {
      case AiReceptionistCallableErrorCode.PermissionDenied:
      case AiReceptionistCallableErrorCode.FailedPrecondition:
        return unavailableCopy;
      case AiReceptionistCallableErrorCode.InvalidArgument:
        return urlRejectionCopy[WebsiteUrlRejectionReason.Invalid];
      default:
        return unknownErrorCopy;
    }
  }
}
